// src/components/trades/components/TradeAssetsSummary.js - COMPACT INLINE VERSION
import React from 'react';
import { ArrowRightLeft, X, DollarSign } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import PlayerLink from '../../PlayerLink';

/**
 * TradeAssetsSummary Component - COMPACT INLINE LAYOUT
 * Displays the center "trade bar" showing assets being traded
 * 
 * Features:
 * - Thin/compact to start, dynamically expands as players added
 * - Cash box on LEFT side (small)
 * - Draft picks INLINE to right of cash box
 * - Players displayed inline
 * - I GIVE section (players, picks, cash)
 * - I GET section (players, picks, cash)
 */
const TradeAssetsSummary = ({
  iGive = { players: [], picks: [], cash: 0 },
  iGet = { players: [], picks: [], cash: 0 },
  onRemoveGivePlayer,
  onRemoveGivePick,
  onRemoveGetPlayer,
  onRemoveGetPick,
  onGiveCashChange,
  onGetCashChange,
  myCashBalance = null,
  theirCashBalance = null,
  league = null
}) => {
  // Get salary increment from league settings (default $2)
  const salaryIncrement = league?.salary_settings?.salary_increment || 
                         league?.minimum_bid || 
                         league?.salary_settings?.min_salary || 
                         2;

  // Round cash value to nearest increment
  const roundToIncrement = (value) => {
    const numValue = parseFloat(value) || 0;
    return Math.round(numValue / salaryIncrement) * salaryIncrement;
  };

  // Handle cash input with rounding
  const handleCashInput = (value, onChange) => {
    const numValue = parseFloat(value) || 0;
    if (numValue > 0) {
      const rounded = roundToIncrement(numValue);
      onChange(rounded);
    } else {
      onChange(0);
    }
  };

  return (
    <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg border-2 ${dynastyTheme.classes.border.primary} p-4`}>
      {/* Header */}
      <div className="text-center mb-4">
        <ArrowRightLeft className={`w-6 h-6 mx-auto ${dynastyTheme.classes.text.primary} mb-1`} />
        <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
          TRADE ASSETS
        </h3>
      </div>

      {/* I GIVE - Compact Inline */}
      <div className="mb-4">
        <div className={`text-xs font-bold text-orange-400 mb-2 flex items-center justify-between`}>
          <span>I GIVE →</span>
          <span className="text-gray-400">
            {iGive.players.length + iGive.picks.length + (iGive.cash > 0 ? 1 : 0)} assets
          </span>
        </div>
        
        <div className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkCard} min-h-[60px]`}>
          <div className="flex flex-wrap gap-2 items-start">
            {/* Cash Box - LEFT SIDE */}
            <div className="flex-shrink-0 w-32">
              <label className="flex items-center gap-1 text-[10px] text-gray-400 mb-1">
                <DollarSign className="w-3 h-3" />
                <span>FAAB/Cash</span>
              </label>
              <input
                type="number"
                min={salaryIncrement}
                max={myCashBalance !== null ? myCashBalance : undefined}
                step={salaryIncrement}
                value={iGive.cash || ''}
                onChange={(e) => handleCashInput(e.target.value, onGiveCashChange)}
                onBlur={(e) => {
                  if (e.target.value) {
                    const rounded = roundToIncrement(e.target.value);
                    if (rounded !== iGive.cash) {
                      onGiveCashChange(rounded);
                    }
                  }
                }}
                placeholder="0.00"
                className={`
                  w-full px-2 py-1 rounded text-xs
                  ${dynastyTheme.classes.bg.neutral}
                  ${dynastyTheme.classes.text.white}
                  border ${
                    myCashBalance !== null && iGive.cash > myCashBalance
                      ? 'border-red-500'
                      : dynastyTheme.classes.border.neutral
                  }
                  focus:outline-none focus:ring-1 focus:ring-yellow-400/50
                `}
              />
              {myCashBalance !== null && (
                <div className={`text-[9px] mt-0.5 ${
                  iGive.cash > myCashBalance ? 'text-red-400' : 'text-green-400'
                }`}>
                  Avail: ${myCashBalance.toFixed(2)}
                </div>
              )}
            </div>
            
            {/* Draft Picks - INLINE */}
            {iGive.picks.map(pick => (
              <InlinePickChip
                key={pick.pick_id}
                pick={pick}
                onRemove={() => onRemoveGivePick(pick.pick_id)}
              />
            ))}
            
            {/* Players - INLINE */}
            {iGive.players.map(player => (
              <InlinePlayerChip
                key={player.ids?.mlb}
                player={player}
                onRemove={() => onRemoveGivePlayer(player.ids?.mlb)}
              />
            ))}
            
            {/* Empty State */}
            {iGive.players.length === 0 && iGive.picks.length === 0 && iGive.cash === 0 && (
              <div className="flex-1 text-center text-gray-500 text-xs py-2">
                Add assets from above →
              </div>
            )}
          </div>
        </div>
      </div>

      {/* I GET - Compact Inline */}
      <div>
        <div className={`text-xs font-bold text-green-400 mb-2 flex items-center justify-between`}>
          <span>← I GET</span>
          <span className="text-gray-400">
            {iGet.players.length + iGet.picks.length + (iGet.cash > 0 ? 1 : 0)} assets
          </span>
        </div>
        
        <div className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkCard} min-h-[60px]`}>
          <div className="flex flex-wrap gap-2 items-start">
            {/* Cash Box - LEFT SIDE */}
            <div className="flex-shrink-0 w-32">
              <label className="flex items-center gap-1 text-[10px] text-gray-400 mb-1">
                <DollarSign className="w-3 h-3" />
                <span>FAAB/Cash</span>
              </label>
              <input
                type="number"
                min={salaryIncrement}
                max={theirCashBalance !== null ? theirCashBalance : undefined}
                step={salaryIncrement}
                value={iGet.cash || ''}
                onChange={(e) => handleCashInput(e.target.value, onGetCashChange)}
                onBlur={(e) => {
                  if (e.target.value) {
                    const rounded = roundToIncrement(e.target.value);
                    if (rounded !== iGet.cash) {
                      onGetCashChange(rounded);
                    }
                  }
                }}
                placeholder="0.00"
                className={`
                  w-full px-2 py-1 rounded text-xs
                  ${dynastyTheme.classes.bg.neutral}
                  ${dynastyTheme.classes.text.white}
                  border ${
                    theirCashBalance !== null && iGet.cash > theirCashBalance
                      ? 'border-red-500'
                      : dynastyTheme.classes.border.neutral
                  }
                  focus:outline-none focus:ring-1 focus:ring-yellow-400/50
                `}
              />
              {theirCashBalance !== null && (
                <div className={`text-[9px] mt-0.5 ${
                  iGet.cash > theirCashBalance ? 'text-red-400' : 'text-green-400'
                }`}>
                  Avail: ${theirCashBalance.toFixed(2)}
                </div>
              )}
            </div>
            
            {/* Draft Picks - INLINE */}
            {iGet.picks.map(pick => (
              <InlinePickChip
                key={pick.pick_id}
                pick={pick}
                onRemove={() => onRemoveGetPick(pick.pick_id)}
              />
            ))}
            
            {/* Players - INLINE */}
            {iGet.players.map(player => (
              <InlinePlayerChip
                key={player.ids?.mlb}
                player={player}
                onRemove={() => onRemoveGetPlayer(player.ids?.mlb)}
              />
            ))}
            
            {/* Empty State */}
            {iGet.players.length === 0 && iGet.picks.length === 0 && iGet.cash === 0 && (
              <div className="flex-1 text-center text-gray-500 text-xs py-2">
                ← Add assets from below
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline Player Chip Component
const InlinePlayerChip = ({ player, onRemove }) => (
  <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-gray-800/70 border ${dynastyTheme.classes.border.primary} group hover:border-yellow-400`}>
    <div className="flex flex-col min-w-0">
      <div className="text-xs font-medium text-white truncate max-w-[120px]">
        <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name || player.info?.last_name || player.info?.name || 'Unknown'} style={{ fontWeight: 500, fontSize: '0.75rem', color: '#ffffff' }} />
      </div>
      <div className="text-[9px] text-gray-400">
        {player.info?.position}
      </div>
    </div>
    <button
      onClick={onRemove}
      className="p-0.5 rounded-full hover:bg-red-500/20 text-red-400 transition-colors flex-shrink-0"
    >
      <X className="w-3 h-3" />
    </button>
  </div>
);

// Inline Pick Chip Component
const InlinePickChip = ({ pick, onRemove }) => {
  const isML = pick.pick_type === 'ml';
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-gray-800/70 border ${isML ? 'border-purple-500' : dynastyTheme.classes.border.primary} group hover:border-yellow-400`}>
      <div className="text-xs text-white whitespace-nowrap">
        {isML && <span className="text-purple-400 font-bold mr-1">ML</span>}
        <span className="font-bold">{pick.year}</span> R<span className="font-bold">{pick.round}</span>
      </div>
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-red-500/20 text-red-400 transition-colors flex-shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export default TradeAssetsSummary;
